import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Apple, ArrowRight, Instagram } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { SHOPIFY_COLLECTION_URL } from "@/lib/api";
import { SpotifyGlyph } from "@/components/SocialLinks";
import { AppleMusicLogo, SpotifyLogo, YouTubeLogo, InstagramLogo } from "@/components/PlatformLogos";
import { OFFICIAL_LINKS } from "@/lib/link";

const HERO_SLIDES = [
  { url: "/carousel/hero-1.webp", alt: "CHILLWOAH — Music, Culture, Community. Two models wearing the XRABI drop: black hoodie, shorts, Just The Tip graphic tee and trucker cap. Shop XRABI — the drop, limited release." },
  { url: "/carousel/hero-2.webp", alt: "Okaytwitchy performing live — stream Okaytwitchy on all platforms: Apple Music, Spotify and YouTube Music." },
];

function YouTubeMusicGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M10 8.5v7l6-3.5-6-3.5z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function Hero() {
  const { content } = useContent();
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);
  const touchX = useRef(null);

  useEffect(() => {
    if (HERO_SLIDES.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 4500);
    return () => clearInterval(t);
  }, []);

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx < -40) setSlide((s) => (s + 1) % HERO_SLIDES.length);
    else if (dx > 40) setSlide((s) => (s - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
    touchX.current = null;
  };

  return (
    <section data-testid="hero" className="relative bg-[#08080A] overflow-hidden grain">
      {/* Signature on-load wipe */}
      <motion.div
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[3px] bg-[#F26522] origin-left z-20"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* HERO BANNER — rotates the uploaded slides, crossfade, swipe on iPhone */}
      <div
        data-testid="hero-carousel"
        className="relative w-full overflow-hidden bg-[#08080A] select-none"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="relative w-full aspect-[1672/941]">
          {HERO_SLIDES.map((s, i) => (
            <img
              key={s.url}
              data-testid={`hero-slide-${i + 1}`}
              src={s.url}
              alt={i === slide ? s.alt : ""}
              fetchPriority={i === 0 ? "high" : undefined}
              loading={i === 0 ? "eager" : "lazy"}
              draggable={false}
              className="absolute inset-0 w-full h-full object-contain transition-opacity duration-[1400ms] ease-in-out"
              style={{ opacity: i === slide ? 1 : 0 }}
            />
          ))}
          {HERO_SLIDES.length > 1 && (
            <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-8 flex gap-2">
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  data-testid={`hero-slide-dot-${i + 1}`}
                  onClick={() => setSlide(i)}
                  aria-label={`Show hero image ${i + 1} of ${HERO_SLIDES.length}`}
                  aria-current={i === slide}
                  className={`h-1.5 rounded-full transition-all duration-500 ${i === slide ? "w-10 bg-[#F26522]" : "w-5 bg-white/50 hover:bg-white/80"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-32 sm:pb-36"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <MaskedLines
          lines={["MUSIC • CULTURE • COMMUNITY"]}
          lineClassName="font-mono text-xs sm:text-sm font-semibold tracking-[0.35em] uppercase text-[#F26522]"
        />
        <h1 className="mt-5">
          <MaskedLines
            delay={0.15}
            lines={["OKAYTWITCHY"]}
            lineClassName="font-display font-extrabold uppercase leading-[0.95] tracking-tight text-white text-[clamp(1.6rem,6.4vw,6.1rem)]"
          />
          <MaskedLines
            delay={0.3}
            lines={["CHILLWOAH"]}
            lineClassName="font-display font-extrabold uppercase leading-[0.95] tracking-tight text-[clamp(1.6rem,6.4vw,6.1rem)] text-outline"
          />
        </h1>
        <MaskedLines
          delay={0.5}
          lines={["INDEPENDENT SOUND. DISTINCT IDENTITY."]}
          lineClassName="mt-6 font-editorial italic text-2xl sm:text-3xl text-[#F5E6D3]"
        />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-10 flex flex-col gap-6"
        >
          <button
            data-testid="hero-shop-xrabi-button"
            onClick={() => window.open(SHOPIFY_COLLECTION_URL, "_blank", "noopener,noreferrer")}
            className="btn-lux w-full sm:w-auto self-stretch sm:self-start min-h-[64px] h-auto py-4 px-10 text-black font-display font-extrabold uppercase tracking-widest text-base sm:text-lg flex items-center justify-center gap-3"
          >
            SHOP XRABI
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </button>

          <div className="flex flex-col items-center gap-2">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-zinc-300 text-center mb-4">
              Listen &amp; follow — official platforms
            </p>

            <div className="grid grid-cols-2 gap-x-14 gap-y-9 justify-items-center">
              {[
                { id: "apple-music", label: "Apple Music", url: OFFICIAL_LINKS.apple_music, Logo: AppleMusicLogo },
                { id: "spotify", label: "Spotify", url: OFFICIAL_LINKS.spotify, Logo: SpotifyLogo },
                { id: "youtube", label: "YouTube", url: OFFICIAL_LINKS.youtube, Logo: YouTubeLogo },
                { id: "instagram", label: "Instagram", url: OFFICIAL_LINKS.instagram, Logo: InstagramLogo },
              ].map((p) => (
                <button
                  key={p.id}
                  data-testid={`hero-icon-${p.id}`}
                  onClick={() => window.open(p.url, "_blank", "noopener,noreferrer")}
                  aria-label={`Okaytwitchy on ${p.label}`}
                  className="platform-logo flex flex-col items-center gap-2.5 group"
                >
                  <p.Logo className="w-[80px] h-[80px] sm:w-24 sm:h-24" />
                  <span className="font-mono text-[10px] sm:text-[11px] font-semibold tracking-[0.2em] uppercase text-zinc-100 group-hover:text-white transition-colors">
                    {p.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="hero-passport-link"
            onClick={() => navigate("/passport")}
            className="passport-lux self-start min-h-[56px] h-auto py-3 px-7 rounded-sm font-mono text-sm font-semibold uppercase tracking-[0.25em] text-[#F5E6D3] flex items-center gap-3"
          >
            ENTER THE PASSPORT
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </button>
        </motion.div>
      </div>

      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="relative z-10 hidden sm:flex items-center gap-4 pb-8 max-w-7xl mx-auto w-full px-6 lg:px-8"
      >
        <span className="font-mono text-xs tracking-[0.35em] text-zinc-300 uppercase">Scroll</span>
        <span className="block w-16 h-px bg-[#F26522]" />
      </motion.div>
    </section>
  );
}
